/** True when entry line matches ``market_quotes.line`` (float-safe). */
export function lineMatchesQuote(quoteLine, outcomeValue) {
  if (quoteLine == null || outcomeValue == null) return false;
  const a = Number(quoteLine);
  const b = Number(outcomeValue);
  if (!Number.isFinite(a) || !Number.isFinite(b)) return false;
  return Math.abs(a - b) < 1e-6;
}

/** Show chevron when there is more than one priced line, or a single off-quote line. */
export function showLineBreakdownExpand(lineBreakdown, quoteLine) {
  const xs = lineBreakdown || [];
  if (!xs.length) return false;
  if (xs.length > 1) return true;
  return !lineMatchesQuote(quoteLine, xs[0].outcome_value);
}

export function fmtLineVal(v) {
  if (v == null || v === '') return '—';
  const n = typeof v === 'number' ? v : Number(String(v).trim());
  if (!Number.isFinite(n)) return String(v);
  if (Number.isInteger(n) || Math.abs(n - Math.round(n)) < 1e-6) return String(Math.round(n));
  return n.toFixed(2);
}

/** Dominant leg by |net|; keeps sign so Li★ can color red vs blue. */
function fmtLiStar(ln, fmtPx) {
  const a = Number(ln.liability_net_over_window);
  const b = Number(ln.liability_net_under_window);
  const o = Number.isFinite(a) ? a : 0;
  const u = Number.isFinite(b) ? b : 0;
  const dom = Math.abs(o) >= Math.abs(u) ? o : u;
  return fmtPx(dom);
}

function dominantSignedLiability(ln) {
  const a = Number(ln.liability_net_over_window);
  const b = Number(ln.liability_net_under_window);
  const o = Number.isFinite(a) ? a : 0;
  const u = Number.isFinite(b) ? b : 0;
  return Math.abs(o) >= Math.abs(u) ? o : u;
}

/**
 * Max |value| across rows for given keys.
 * @param primaryKeys  When set, use these instead of default quote-line keys
 *   ``liability_net_over_window`` / ``liability_net_under_window`` (e.g. exposure API fields).
 */
export function maxAbsLiabilityFromRows(rows, extraKeys = [], primaryKeys = null) {
  let m = 0;
  const base =
    Array.isArray(primaryKeys) && primaryKeys.length
      ? primaryKeys
      : ['liability_net_over_window', 'liability_net_under_window'];
  const keys = [...base, ...extraKeys];
  for (const row of rows || []) {
    for (const k of keys) {
      const v = Number(row[k]);
      if (Number.isFinite(v)) m = Math.max(m, Math.abs(v));
    }
  }
  return m;
}

/** Red vs light blue from sign; font-weight from |value| vs table max. */
export function liabilitySignClass(value) {
  const v = Number(value);
  if (!Number.isFinite(v)) return 'markets-li-sign--zero';
  if (v > 1e-9) return 'markets-li-sign--pos';
  if (v < -1e-9) return 'markets-li-sign--neg';
  return 'markets-li-sign--zero';
}

export function liabilityWeightClass(value, maxAbs) {
  const v = Number(value);
  const av = Number.isFinite(v) ? Math.abs(v) : 0;
  if (!Number.isFinite(maxAbs) || maxAbs < 1e-9) return 'markets-li-wgt--neu';
  const r = av / maxAbs;
  if (r >= 0.72) return 'markets-li-wgt--high';
  if (r >= 0.22) return 'markets-li-wgt--mid';
  return 'markets-li-wgt--low';
}

/** Signed liability cells (LiO, LiU, Li★ when showing dominant signed leg). */
export function liabilityCellClasses(value, maxAbs) {
  return `${liabilitySignClass(value)} ${liabilityWeightClass(value, maxAbs)}`.trim();
}

/** Non-negative exposure only (e.g. max-abs column): red scale by magnitude. */
export function liabilityExposureCellClasses(value, maxAbs) {
  const v = Number(value);
  const av = Number.isFinite(v) ? Math.abs(v) : 0;
  const sign = av < 1e-9 ? 'markets-li-sign--zero' : 'markets-li-sign--pos';
  return `${sign} ${liabilityWeightClass(av, maxAbs)}`.trim();
}

function maxAbsLiabilityInBreakdown(rows) {
  return maxAbsLiabilityFromRows(rows);
}

/** Dropdown metric value: tier + fixed width shell (line-detail row). */
function dropdownLiabilityValueClass(value, maxAbs) {
  return `markets-line-metric-val markets-line-num-w6 ${liabilityCellClasses(value, maxAbs)}`;
}

function Metric({ label, children, valueClassName }) {
  return (
    <span className="markets-line-metric">
      <span className="markets-line-metric-label">{label}</span>
      <span className={valueClassName || 'markets-line-metric-val markets-line-num-w6'}>{children}</span>
    </span>
  );
}

/** Markets Summary: 16 columns — expand, 6 metrics, 9 trailing. */
export function MarketsSummaryLineRows({ quoteLine, lineBreakdown, fmtPx }) {
  const rows = lineBreakdown || [];
  const liMax = maxAbsLiabilityInBreakdown(rows);
  return rows.map((ln, i) => (
    <tr key={`ln-${i}-${ln.outcome_value}`} className="markets-row-line-detail">
      <td />
      <td colSpan={6} className="markets-line-detail-cell">
        <span className="markets-line-detail-rowhead">
          <span className="markets-line-detail-prefix">Line</span>
          <span className="markets-line-detail-num markets-line-num-w6">{fmtLineVal(ln.outcome_value)}</span>
          <span className="markets-line-quote-slot">
            {lineMatchesQuote(quoteLine, ln.outcome_value) ? (
              <span className="markets-line-quote-pill">quote</span>
            ) : (
              <span className="markets-line-quote-slot--empty" aria-hidden="true" />
            )}
          </span>
        </span>
        <span className="markets-line-detail-metrics">
          <Metric label="Σ">{fmtPx(ln.fees_window)}</Metric>
          <Metric label="O">{fmtPx(ln.fee_over_window)}</Metric>
          <Metric label="U">{fmtPx(ln.fee_under_window)}</Metric>
          <Metric label="LiO" valueClassName={dropdownLiabilityValueClass(ln.liability_net_over_window, liMax)}>
            {fmtPx(ln.liability_net_over_window)}
          </Metric>
          <Metric label="LiU" valueClassName={dropdownLiabilityValueClass(ln.liability_net_under_window, liMax)}>
            {fmtPx(ln.liability_net_under_window)}
          </Metric>
          <Metric label="n">{ln.n_entries_window != null ? ln.n_entries_window : '—'}</Metric>
        </span>
      </td>
      <td colSpan={9} />
    </tr>
  ));
}

/** Sleeper models table: 13 columns. */
export function SleeperMarketsLineRows({ quoteLine, lineBreakdown, fmtPx }) {
  const rows = lineBreakdown || [];
  const liMax = maxAbsLiabilityInBreakdown(rows);
  return rows.map((ln, i) => (
    <tr key={`dln-${i}-${ln.outcome_value}`} className="markets-row-line-detail">
      <td />
      <td />
      <td>
        <span className={dropdownLiabilityValueClass(dominantSignedLiability(ln), liMax)}>
          {fmtLiStar(ln, fmtPx)}
        </span>
      </td>
      <td>
        <span className={dropdownLiabilityValueClass(ln.liability_net_over_window, liMax)}>
          {fmtPx(ln.liability_net_over_window)}
        </span>
      </td>
      <td>
        <span className={dropdownLiabilityValueClass(ln.liability_net_under_window, liMax)}>
          {fmtPx(ln.liability_net_under_window)}
        </span>
      </td>
      <td>
        <span className="markets-line-num-w6">{fmtPx(ln.fees_window)}</span>
      </td>
      <td>
        <span className="markets-line-num-w6">
          {ln.n_entries_window != null ? ln.n_entries_window : '—'}
        </span>
      </td>
      <td colSpan={6} className="mk-muted markets-line-detail-cell">
        <span className="markets-line-detail-rowhead">
          <span className="markets-line-detail-prefix">Line</span>
          <span className="markets-line-detail-num markets-line-num-w6">{fmtLineVal(ln.outcome_value)}</span>
          <span className="markets-line-quote-slot">
            {lineMatchesQuote(quoteLine, ln.outcome_value) ? (
              <span className="markets-line-quote-pill">quote</span>
            ) : (
              <span className="markets-line-quote-slot--empty" aria-hidden="true" />
            )}
          </span>
        </span>
      </td>
    </tr>
  ));
}
